import theme from 'config/theme'

export type PoiLabelPosition = 'right' | 'top' | 'bottom'

export interface LabelPlacement {
  position: PoiLabelPosition
  lines?: string[]
}

interface LabelCandidate {
  id: string
  point: { x: number; y: number }
  name?: string
}

interface Rect {
  x: number
  y: number
  width: number
  height: number
}

export const POI_ICON_SIZE = 24
export const POI_LABEL_GAP = 4

const ICON_SIZE = POI_ICON_SIZE
const LABEL_GAP = POI_LABEL_GAP
const LABEL_FONT_SIZE = 12
const LABEL_FONT = `${LABEL_FONT_SIZE}px ${theme.typography.fontFamily}`
const LABEL_LINE_HEIGHT = LABEL_FONT_SIZE * Number(theme.typography.lineHeight)
const WRAP_MAX_WIDTH = 110
const LABEL_STROKE_PADDING = 2
const POSITION_LADDER: PoiLabelPosition[] = ['right', 'top', 'bottom']

const AVERAGE_CHAR_WIDTH = LABEL_FONT_SIZE * 0.6

let measureContext: CanvasRenderingContext2D | null | undefined

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureContext === undefined) {
    measureContext = document.createElement('canvas').getContext('2d')
    if (measureContext) {
      measureContext.font = LABEL_FONT
    }
  }
  return measureContext
}

function measureTextWidth(text: string): number {
  const context = getMeasureContext()
  return context ? context.measureText(text).width : text.length * AVERAGE_CHAR_WIDTH
}

function wrapText(text: string, maxWidth: number): string[] {
  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word
    if (!currentLine || measureTextWidth(candidate) <= maxWidth) {
      currentLine = candidate
    } else {
      lines.push(currentLine)
      currentLine = word
    }
  })

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y
}

function iconRect(point: { x: number; y: number }): Rect {
  return { x: point.x - ICON_SIZE / 2, y: point.y - ICON_SIZE / 2, width: ICON_SIZE, height: ICON_SIZE }
}

function candidateRect(
  point: { x: number; y: number },
  position: PoiLabelPosition,
  width: number,
  height: number
): Rect {
  switch (position) {
    case 'right':
      return { x: point.x + ICON_SIZE / 2 + LABEL_GAP, y: point.y - height / 2, width, height }
    case 'top':
      return { x: point.x - width / 2, y: point.y - ICON_SIZE / 2 - LABEL_GAP - height, width, height }
    case 'bottom':
      return { x: point.x - width / 2, y: point.y + ICON_SIZE / 2 + LABEL_GAP, width, height }
  }
}

interface Candidate {
  position: PoiLabelPosition
  rect: Rect
  lines?: string[]
}

function overlapsAny(rect: Rect, rects: Rect[]): boolean {
  return rects.some((other) => rectsIntersect(rect, other))
}

function buildCandidates(marker: LabelCandidate, name: string): Candidate[] {
  const singleLineWidth = measureTextWidth(name) + 2 * LABEL_STROKE_PADDING
  const singleLineHeight = LABEL_LINE_HEIGHT + 2 * LABEL_STROKE_PADDING
  const ladderCandidates = POSITION_LADDER.map((position) => ({
    position,
    rect: candidateRect(marker.point, position, singleLineWidth, singleLineHeight),
  }))

  const lines = wrapText(name, WRAP_MAX_WIDTH)
  if (lines.length <= 1) {
    return ladderCandidates
  }

  const wrapWidth = Math.max(...lines.map(measureTextWidth)) + 2 * LABEL_STROKE_PADDING
  const wrapHeight = lines.length * LABEL_LINE_HEIGHT + 2 * LABEL_STROKE_PADDING
  const wrapCandidate: Candidate = {
    position: 'bottom',
    rect: candidateRect(marker.point, 'bottom', wrapWidth, wrapHeight),
    lines,
  }

  return [...ladderCandidates, wrapCandidate]
}

export interface Viewport {
  width: number
  height: number
}

function isOutsideViewport(rect: Rect, viewport: Viewport): boolean {
  return rect.x < 0 || rect.y < 0 || rect.x + rect.width > viewport.width || rect.y + rect.height > viewport.height
}

function isIconInsideViewport(icon: Rect, viewport: Viewport): boolean {
  return rectsIntersect(icon, { x: 0, y: 0, width: viewport.width, height: viewport.height })
}

function pickBestCandidate(candidates: Candidate[], labelRects: Rect[], viewport?: Viewport): Candidate {
  let best = candidates[0]
  let bestScore = Infinity

  for (const candidate of candidates) {
    const score =
      (overlapsAny(candidate.rect, labelRects) ? 1 : 0) +
      (viewport && isOutsideViewport(candidate.rect, viewport) ? 1 : 0)

    if (score < bestScore) {
      best = candidate
      bestScore = score
      if (score === 0) {
        break
      }
    }
  }

  return best
}

export function computeLabelPlacements(markers: LabelCandidate[], viewport?: Viewport): Map<string, LabelPlacement> {
  const placements = new Map<string, LabelPlacement>()
  const iconRects: Rect[] = markers.map((marker) => iconRect(marker.point))
  const labelRects: Rect[] = []

  markers.forEach((marker, index) => {
    if (!marker.name || (viewport && !isIconInsideViewport(iconRects[index], viewport))) {
      return
    }

    const candidates = buildCandidates(marker, marker.name)

    const iconSafeCandidates = candidates.filter((candidate) => !overlapsAny(candidate.rect, iconRects))
    if (iconSafeCandidates.length === 0) {
      // nenhuma posição livre de ícones de outros POIs: esconde o label pra nunca cobrir outro marker
      return
    }

    const chosen = pickBestCandidate(iconSafeCandidates, labelRects, viewport)

    placements.set(marker.id, { position: chosen.position, lines: chosen.lines })
    labelRects.push(chosen.rect)
  })

  return placements
}
